/**
 * fontLicense/LicenseConfirmationService.ts
 *
 * Promise-based facade for the license confirmation modal. Both integration
 * points (the Asset Lab upload hook and the Creative Hub async listener)
 * `await` the returned Promise, so a single facade serves both.
 *
 * Usage:
 *   const submission = await requestLicenseConfirmation(fonts, signal);
 *   if (!submission) return; // user cancelled (or upload aborted) -> abort upload
 *   // submission is a Record<fileKey, LicenseInfo>
 *
 * Mirrors creativeWorkflow/metadataEditor/MetadataEditorService.ts.
 */

import { FontFileEntry, LicenseSubmission } from './model';

type DispatchFn = (action: any) => void;

let _dispatch: DispatchFn | null = null;
let _pendingResolve: ((result: LicenseSubmission | null) => void) | null = null;
/** Removes the abort listener attached for the current request, if any. */
let _abortCleanup: (() => void) | null = null;

export function _registerDispatch(dispatch: DispatchFn) {
  _dispatch = dispatch;
}

export function _unregisterDispatch() {
  _dispatch = null;
  // Provider unmounting: don't leave a hanging Promise behind. Resolve any
  // in-flight request as cancelled so the awaiting caller unblocks and aborts.
  _resolveWithCancel();
}

/** Settles the pending request exactly once and tears down its abort listener. */
function _settle(result: LicenseSubmission | null) {
  if (_abortCleanup) {
    _abortCleanup();
    _abortCleanup = null;
  }
  if (_pendingResolve) {
    const resolve = _pendingResolve;
    _pendingResolve = null;
    resolve(result);
  }
}

/** Called by the modal's Submit button once the form is valid. */
export function _resolveWithLicenses(submission: LicenseSubmission) {
  _settle(submission);
}

/** Called by the modal's Cancel/Close (blocking form -> aborts the upload). */
export function _resolveWithCancel() {
  _settle(null);
}

/**
 * Open the license confirmation modal and wait for the user.
 *
 * @param fonts detected fonts to collect licenses for.
 * @param signal optional AbortSignal. While the modal is open, an external abort
 *   (e.g. "Cancel All") closes it and resolves the Promise with null, so the
 *   caller doesn't stay blocked on user input that will never come.
 * @returns LicenseSubmission if the user submitted, null if cancelled/aborted.
 * @throws if the modal is not mounted (FontLicenseProvider missing).
 */
export async function requestLicenseConfirmation(
  fonts: FontFileEntry[],
  signal?: AbortSignal,
): Promise<LicenseSubmission | null> {
  if (!_dispatch) {
    throw new Error(
      'LicenseConfirmationService: modal not mounted. Wrap your app in <FontLicenseProvider>.',
    );
  }

  // If a previous request is still pending, resolve it as cancelled to avoid leaks.
  if (_pendingResolve) {
    _resolveWithCancel();
  }

  // Already aborted before we even open — don't show the modal at all.
  if (signal?.aborted) {
    return null;
  }

  _dispatch({ type: 'OPEN', fonts });

  return new Promise<LicenseSubmission | null>((resolve) => {
    _pendingResolve = resolve;

    if (signal) {
      const onAbort = () => {
        _dispatch?.({ type: 'CLOSE' });
        _resolveWithCancel();
      };
      signal.addEventListener('abort', onAbort, { once: true });
      _abortCleanup = () => signal.removeEventListener('abort', onAbort);
    }
  });
}
